import * as React from "react";
import { alpha, styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { TreeView } from '@mui/x-tree-view/TreeView';
import { TreeItem, TreeItemProps, treeItemClasses } from '@mui/x-tree-view/TreeItem';
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";
import styledComp from "styled-components";
import { useEffect, useMemo } from "react";
import { Container, useScreenClass } from "react-grid-system";
import { useLocation, useNavigate } from "react-router-dom";
import FlexBox from "./FlexBox";
import { ClickableIcon } from "./Icon";
declare module "react" {
  interface CSSProperties {
    "--tree-view-color"?: string;
    "--tree-view-bg-color"?: string;
  }
}
const ChildArea = styledComp.div`
  max-width: 75%;
  min-width: 75%;
  transition: all ${({ theme }) => theme.transitionTime.default};
`;
const StyledContainer = styledComp(Container)`
padding: 0 !important;
`;
export type DropdownProps = {
  data: any;
  expandedId?: string;
};

type StyledTreeItemProps = TreeItemProps & {
  bgColor?: string;
  color?: string;
  iconBg?: string;
  borderClr?: string;
  parent: boolean;
  iconShadow?: string;
  icon?: string;
  labelInfo?: string;
  lastItem?: boolean;
  labelText: string;
  showLabel: boolean;
};
type StyledTreeItemRootProps = TreeItemProps & {
  borderClr?: string;
};
const StyledHr = styledComp.hr(({ color }) => ({
  width: "10px",
  marginRight: "5px",
  borderColor: color,
  height: "1px",
  border: 0,
  background: color,
}));
const StyledTreeItemRoot = styled(TreeItem)<any>(({ theme, borderClr }) => ({
  color: "#fff",
  fontFamily: "Inter",
  [`.${treeItemClasses.iconContainer}`]: {
    display: "none !important",
  },
  [`& .${treeItemClasses.content}`]: {
    color: "#9295AA",
    borderTopRightRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
    paddingRight: theme.spacing(0),
    padding: theme.spacing(0),
    fontFamily: "Inter",
    fontWeight: theme.typography.fontWeightMedium,
    "&.Mui-expanded": {
      fontWeight: theme.typography.fontWeightRegular,
    },
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused": {
      backgroundColor: `var(--tree-view-bg-color, ${theme.palette.action.selected})`,
      color: "var(--tree-view-color)",
    },
    [`& .${treeItemClasses.label}`]: {
      fontFamily: "Inter",
      fontWeight: "inherit",
      color: "inherit",
      padding: 0,
    },
  },
  // [`& .${treeItemClasses.group}`]: {
  //   marginLeft: 0,
  //   [`& .${treeItemClasses.content}`]: {
  //     paddingLeft: theme.spacing(2),
  //   },
  // },
  [`& .${treeItemClasses.group}`]: {
    marginLeft: 14,
    paddingLeft: 0,
    // borderLeft: `3px solid ${borderClr}`,
  },
  [`& .${treeItemClasses.iconContainer}`]: {
    display: "none",
  },
}));
function StyledTreeItem(props: StyledTreeItemProps) {
  const {
    bgColor,
    color,
    iconBg,
    borderClr,
    parent,
    iconShadow,
    icon,
    labelInfo,
    labelText,
    lastItem,
    showLabel,
    ...other
  } = props;

  return (
    <StyledTreeItemRoot
      borderClr={borderClr}
      label={
        <>
          {icon && parent ? (
            <Box sx={{ display: "flex", alignItems: "center", p: 0, pr: 0 }}>
              <Icon
                sx={{
                  color: "white",
                  mr: 1,
                  padding: "6px",
                  background: iconBg,
                  boxShadow: iconShadow,
                  borderRadius: "100px",
                  fontSize: "18px",
                }}
              >
                {icon}
              </Icon>
              {showLabel && (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "inherit",
                      flexGrow: 1,
                      fontFamily: "Inter",
                    }}
                  >
                    {labelText}
                  </Typography>
                  <Typography variant="caption" color="inherit">
                    {labelInfo}
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 0,
                pr: 0,
                "&:before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  zIndex: "100",
                  top: 0,
                  width: "3px",
                  height: lastItem ? "50%" : "100%",
                  background: borderClr,
                },
              }}
            >
              <StyledHr color={borderClr} />
              {showLabel && (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "inherit",
                    flexGrow: 1,
                    fontFamily: "Inter",
                  }}
                >
                  {labelText}
                </Typography>
              )}
              {/* <Typography variant="caption" color="inherit">
                {labelInfo}
              </Typography> */}
            </Box>
          )}
        </>
      }
      // style={{
      //   '--tree-view-color': color,
      //   '--tree-view-bg-color': bgColor,
      // }}
      {...other}
    />
  );
}

const Tree = ({ data, expandedId, ...props }: DropdownProps): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = React.useState<string[]>([]);
  const [selected, setSelected] = React.useState<string>("");
  const screenClass = useScreenClass();
  const isMobile = useMemo(
    () => ["xs", "sm"].includes(screenClass),
    [screenClass]
  );
  const [isSidebarOpen, setSidebarOpen] = React.useState<boolean>(false);

  const handleToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
    const customNode = [];
    customNode.push(nodeIds[0]);
    setExpanded(customNode);
  };

  const handleSelect = (event: React.SyntheticEvent, nodeIds: string) => {
    const customNode = [];
    customNode.push(nodeIds[0]);
    setSelected(nodeIds);
  };

  const handleExpandClick = (nodeId: string) => {
    setExpanded([nodeId]);
  };

  const redirectDetail = (id: string, node: string) => {
    setSidebarOpen(false);
    navigate("/explore/child", { state: { id, node } });
  };
  const redirectChild = (idChild: string, node: string, pillars: any) => {
    setSidebarOpen(false);
    const selectedChild = pillars.children.filter(
      (el: { id: string }) => el.id === idChild
    )[0];
    navigate("/explore/child/competencies", {
      state: { id: idChild, node, parent: pillars, child: selectedChild },
    });
  };
  useEffect(() => {
    if (location && location.pathname === "/explore") {
      handleExpandClick("");
      setSelected("");
    }
    if (location && location.pathname === "/explore/child") {
      handleExpandClick(String((location as any).state?.node));
      setSelected(String((location as any).state?.node));
      handleToggle(
        undefined as unknown as React.SyntheticEvent<Element, Event>,
        [String((location as any).state?.node)]
      );
    }
    if (
      location &&
      location.pathname === "/explore/child/competencies" &&
      selected.length < 2
    ) {
      setSelected(String((location as any).state?.node));
      // handleToggle(
      //   undefined as unknown as React.SyntheticEvent<Element, Event>,
      //   [String((location as any).state.node)],
      // );
    }
  }, [location]);
  useEffect(() => {
    if (expandedId && expandedId !== "") {
      handleExpandClick(expandedId);
    }
  }, [expandedId]);
  // useEffect(() => {
  //   if (isSidebarOpen) {
  //     setTimeout(() => {
  //       setSidebarOpen(false);
  //     }, 1000);
  //   }
  // }, [isSidebarOpen]);

  return (
    <>
      <TreeView
        aria-label="file system navigator"
        expanded={expanded}
        selected={selected}
        onNodeToggle={handleToggle}
        onNodeSelect={handleSelect}
        sx={{
          height: "100%",
          flexGrow: 1,
          maxWidth: isMobile ? (isSidebarOpen ? "230px" : "35px") : "230px",
          minWidth: isMobile ? (isSidebarOpen ? "230px" : "35px") : "230px",
          overflowY: "auto",
          // position: 'fixed',
          "-webkit-box-flex": 1,
          "-webkit-flex-grow": 1,
          "-ms-flex-positive": 1,
          "-webkit-flex-basis": 0,
          "-ms-flex-preferred-size": 0,
          flexBasis: 0,
        }}
      >
        {isMobile && (
          <>
            {isSidebarOpen ? (
              <>
                <FlexBox>
                  <ClickableIcon
                    onClick={() => {
                      setSidebarOpen(!isSidebarOpen);
                    }}
                    name="left-arrow-to-left"
                  ></ClickableIcon>
                </FlexBox>
              </>
            ) : (
              <FlexBox>
                <ClickableIcon
                  onClick={() => {
                    setSidebarOpen(!isSidebarOpen);
                  }}
                  name="arrow-to-right"
                ></ClickableIcon>
              </FlexBox>
            )}
          </>
        )}
        {data &&
          data.map((treeItem: any) => (
            <StyledTreeItem
              key={treeItem.id}
              onClick={() => {
                redirectDetail(treeItem.id, treeItem.node);
              }}
              style={{ margin: "16px 0px" }}
              showLabel={isMobile ? (isSidebarOpen ? true : false) : true}
              nodeId={String(treeItem.node)}
              labelText={treeItem.label}
              icon={treeItem.icon}
              parent={true}
              iconShadow={treeItem.iconShadow}
              borderClr={treeItem.borderColor}
              iconBg={treeItem.iconBg}
            >
              {treeItem.children &&
                treeItem.children.length > 0 &&
                treeItem.children.map(
                  (child: any, i: number, { length }: any) => (
                    <StyledTreeItem
                      key={child.id}
                      onClick={() => {
                        redirectChild(child.id, treeItem.node, treeItem);
                      }}
                      showLabel={
                        isMobile ? (isSidebarOpen ? true : false) : true
                      }
                      nodeId={String(child.node)}
                      parent={false}
                      labelText={child.label}
                      icon={child?.icon}
                      borderClr={treeItem.borderColor}
                      labelInfo={child.count}
                      lastItem={length - 1 === i ? true : false}
                      color="#1a73e8"
                      bgColor="#e8f0fe"
                    />
                  )
                )}
            </StyledTreeItem>
          ))}
      </TreeView>
    </>
  );
};

export default Tree;
